/**
 * Model enums mirrored from python backend
 * @module
 */
var _ = require('optimizely-client-fn');
var keyMirror = require('keymirror');

/**
 * Cookie names used by the client (shared with legacy client)
 * @enum {string}
 */
exports.COOKIES = {
  OPT_OUT: 'optimizelyOptOut',
  PREVIEW: 'optimizelyPreview',
  REDIRECT: 'optimizelyRedirectData',
  SESSION_STATE: 'optimizelySessionState',
  OPTIMIZELY_SESSION: 'optimizelySession',
  TOKEN: 'optimizelyToken',
  VISITOR_ID: 'optimizelyEndUserId',
  VISITOR_UUID: 'optimizelyPPID',
};

exports.LayerActivationTypes = {
  CONDITIONAL: 'conditional',
  IMMEDIATE: 'immediate',
  MANUAL: 'manual',
  READY: 'ready',
  TIMEOUT: 'timeout',
};

/**
 * @enum {number}
 */
exports.LogLevel = {
  OFF: 0,
  ERROR: 1,
  WARN: 2,
  INFO: 3,
  DEBUG: 4,
};

/**
 * Lifecycle events that can have cleanupFns
 * TODO(tyler): clean this up, enforce linearity and ordering
 * @enum {string}
 */
exports.Lifecycle = keyMirror({
  preActivate: null,
  postVisitorProfileLoad: null,
  postViewsActivated: null,
  postActivate: null,
});

/**
 * View activation types
 * @enum {string}
 */
exports.ViewActivationTypes = {
  immediate: 'immediate',
  manual: 'manual',
  callback: 'callback',
  polling: 'polling',
  URLChanged: 'url_changed',
  DOMChanged: 'dom_changed',
};

/**
 * Storage keys for data stored in localStorage
 * @enum {string}
 */
exports.StorageKeys = {
  PENDING_EVENTS: 'pending_events',
  RELAYED_EVENTS: 'relayed_events',
};

/**
 * Plugin types
 * @enum {string}
 */
exports.PluginTypes = keyMirror({
  visitorProfileProviders: null,
  viewProviders: null,
  audienceMatchers: null,
  viewMatchers: null,
  analyticsTrackers: null,
  viewTagLocators: null,
  userFeatureDefs: null,
  apiModules: null,
  changeAppliers: null,
  deciders: null,
  eventImplementations: null,
  viewTriggers: null,
});

/**
 * Resource timing attributes
 * @enum {string}
 */
exports.ResourceTimingAttributes = keyMirror({
  connectStart: null,
  connectEnd: null,
  decodedBodySize: null,
  domainLookupStart: null,
  domainLookupEnd: null,
  duration: null,
  encodedBodySize: null,
  fetchStart: null,
  requestStart: null,
  responseStart: null,
  responseEnd: null,
  secureConnectionStart: null,
  startTime: null,
  transferSize: null,
  serverTiming: null,
});

/**
 * Performance timing measure names
 * @enum {string}
 */
exports.RUMPerformanceTimingAttributes = keyMirror({
  blockTime: null,
});

/**
 * How to resolve conflicts in cross-origin visitorProfile data
 * @enum {string}
 */
exports.AttributionTypes = keyMirror({
  FIRST_TOUCH: null,
  LAST_TOUCH: null,
});

/**
 * Sandboxed functions
 * keyname must match sandboxed function name
 * @enum {string}
 */
exports.SandboxedFunctions = keyMirror({
  XMLHttpRequest: null,
});

/**
 * Performance Data
 * keyname for data parameters
 * @enum {string}
 */
exports.PerformanceData = keyMirror({
  performance_marks: null,
  resource_timing: null,
  performance_timing: null,
});

/**
 * Performance Counters
 * keyname for counters us
 * @enum {string}
 */
exports.PerformanceCounters = keyMirror({
  mutation_observer_invocation: null,
  polling_invocation: null,
  match_selector_invocation: null,
});

/**
 * Visitor local storage keys
 * For data in localStorage that is keyed to visitorId
 * @enum {string}
 */
exports.VisitorStorageKeys = {
  EVENTS: 'events',
  EVENT_QUEUE: 'event_queue',
  LAYER_MAP: 'layer_map',
  LAYER_STATES: 'layer_states',
  SESSION_STATE: 'session_state',
  VISITOR_PROFILE: 'visitor_profile',
  VARIATION_MAP: 'variation_map',
  TRACKER_OPTIMIZELY: 'tracker_optimizely',
  CONTEXTUAL_MAB: 'contextual_mab',
  ODP_SEGMENTS: 'odp_segments',
};

/**
 * @enum {string}
 */
exports.AllStorageKeys = _.assign({}, exports.StorageKeys, exports.VisitorStorageKeys);

/**
 * List Targeting key types
 * @enum {string}
 */
exports.ListTargetingKeyTypes = {
  COOKIE: 'c',
  QUERY: 'q',
  JS_VARIABLE: 'j',
};

/**
 * VisitorId Locator key types
 * @enum {string}
 */
exports.VisitorIdLocatorType = {
  COOKIE: 'cookie',
  JS_VARIABLE: 'js',
  LOCALSTORAGE: 'localStorage',
  QUERY: 'query',
};

/**
 * Experiment's allocation policy types
 * @enum {string}
 */
exports.AllocationPolicyEnum = {
  CMAB: 'contextual-multi-arm-bandit',
  MIN_REGRET: 'min-regret',
  MIN_TIME: 'min-time',
  MANUAL: 'manual',
};
